// =============================================================================
// runsRegister — the pure half of the Runs register (plan §2D items 2 and 5;
// Jason, 2026-09-07: card AND table views, both packouts side by side at
// equal weight).
//
// The register used to fetch every run in a window with no range and filter
// on the client, so a search could miss runs past the 1,000-row cap. Filters
// now go to the server; this module builds them and shapes what a row shows.
// =============================================================================
import { packoutOfMassOut, rejectCapture, type RejectCapture, type PackoutRun } from '@/lib/growerPackout'
import { POLL } from '@/lib/pollTiers'
import type { Accessors } from '@/hooks/useTableSort'
import { isExternalReceiptRun } from '@/lib/externalReceiptPallets'
import { isSuperAdmin, holdsHardDeleteSeat } from '@/lib/roles'
import type { UserRole } from '@/types/database'
import { PACKING_OPEN_STATUSES } from '@/lib/runStatus'
import { sastYMD, sastTodayYMD } from '@/lib/dates'

type RunSearchField = 'any' | 'run_number' | 'grower' | 'variety' | 'puc' | 'commodity'
const RUN_ANY_FIELD_KEYS: Exclude<RunSearchField, 'any'>[] = ['run_number', 'grower', 'variety', 'puc', 'commodity']

/** A search term made safe for a PostgREST `ilike` pattern inside an `or=()` filter. */
/** A typed term with the characters PostgREST's `or=`/ilike cannot carry turned to spaces. */
export function cleanSearchTerm(term: string): string {
  return term.trim().replace(/[,()"\\%_]/g, ' ').replace(/\s+/g, ' ').trim()
}

export function searchPattern(term: string): string {
  const cleaned = cleanSearchTerm(term)
  return cleaned ? `%${cleaned}%` : ''
}

/** The `or=` filter for an any-field search, or null when there is nothing to search for. */
export function anyFieldOr(term: string): string | null {
  const p = searchPattern(term)
  if (!p) return null
  return RUN_ANY_FIELD_KEYS.map(k => `${k}.ilike.${p}`).join(',')
}

/** A status chip on the register. 'OPEN' is Active OR Tipping stopped together —
 *  the runs still open after tipping stops, which is what Home's "Active runs"
 *  tile counts (get_inventory_pulse: status IN ('ACTIVE', 'TIPPING_COMPLETE');
 *  Jason ruled 8 Oct that its link opens both). One chip value, not a
 *  multi-select, so the device's saved filter stays a single string and any
 *  later chip tap simply replaces it. */
export type RunStatusFilter = 'ALL' | 'ACTIVE' | 'TIPPING_COMPLETE' | 'OPEN' | 'COMPLETE' | 'AWAITING_VERIFY' | 'VERIFIED' | 'ARCHIVED'

/** The register's status chips, in strip order. */
export const RUN_STATUS_CHIPS: readonly { value: RunStatusFilter; label: string }[] = [
  { value: 'ALL',              label: 'All' },
  { value: 'ACTIVE',           label: 'Active' },
  { value: 'TIPPING_COMPLETE', label: 'Tipping stopped' },
  { value: 'OPEN',             label: 'Active or tipping stopped' },
  { value: 'COMPLETE',         label: 'Complete' },
  { value: 'AWAITING_VERIFY', label: 'Awaiting verify' },
  { value: 'VERIFIED',         label: 'Verified' },
  { value: 'ARCHIVED',         label: 'Archived' },
]

const RUN_STATUS_FILTER_VALUES: readonly string[] = RUN_STATUS_CHIPS.map(c => c.value)

/** The `runs.status` values a chip selects, for `.in('status', …)` — null when
 *  the chip is not a status match (All: no filter; Verified: `verified_at`).
 *  'Awaiting verify' is Complete AND `verified_at` null (what Home's "runs
 *  awaiting Manager verify" alert counts): the register adds the null test. */
export function runStatusesFor(filter: RunStatusFilter): readonly string[] | null {
  if (filter === 'ALL' || filter === 'VERIFIED') return null
  if (filter === 'OPEN') return PACKING_OPEN_STATUSES
  if (filter === 'AWAITING_VERIFY') return ['COMPLETE']
  return [filter]
}

/** How often the register re-reads itself (U17 UF-07). Only the chips whose
 *  server filter selects open runs poll (a handful of rows, every minute);
 *  All, Complete, Awaiting verify, Verified and Archived are big reads and
 *  stay on the app-wide sweep. False = no polling. */
export function registerPollMs(filter: RunStatusFilter): number | false {
  return filter === 'ACTIVE' || filter === 'TIPPING_COMPLETE' || filter === 'OPEN' ? POLL.standard : false
}

/** The register on Active + Tipping stopped with the 90-day window lifted —
 *  what Home's "Active runs" tile opens (F16 UF-05; Jason 8 Oct). The tile
 *  counts open runs of ANY age, so the window must go too or an old run that
 *  is still open stays hidden. */
export const RUNS_ACTIVE_ALL_HISTORY_PATH = `/runs?status=${PACKING_OPEN_STATUSES.join(',')}&history=all`

/** What Home's "runs awaiting Manager verify" alert opens (F02 UF-12): Complete
 *  and not yet verified, all history — the alert counts runs of any age. */
export const RUNS_AWAITING_VERIFY_PATH = '/runs?status=AWAITING_VERIFY&history=all'

/** `?status=` as a chip: one known chip value, or a comma list of statuses
 *  that is exactly Active + Tipping complete (any order) → 'OPEN'. Anything
 *  else (an unknown value, some other combination) is not a link. */
function statusFromLink(raw: string | null): RunStatusFilter | null {
  if (!raw) return null
  const parts = [...new Set(raw.split(',').map(s => s.trim()).filter(Boolean))]
  if (parts.length === 1) return RUN_STATUS_FILTER_VALUES.includes(parts[0]) ? parts[0] as RunStatusFilter : null
  const open: readonly string[] = PACKING_OPEN_STATUSES
  return parts.length === open.length && parts.every(p => open.includes(p)) ? 'OPEN' : null
}

/** A link into the register: `?status=<filter>` and/or `?history=all`. Read
 *  ONCE on arrival (the page keeps its status per device and its window in
 *  state, so the link sets them and is then dropped from the address).
 *  null when the address carries neither, or only an unknown status. */
export function readRunsLink(params: URLSearchParams): { status: RunStatusFilter | null; allHistory: boolean } | null {
  const status = statusFromLink(params.get('status'))
  const allHistory = params.get('history') === 'all'
  return status || allHistory ? { status, allHistory } : null
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The link that opens New Run (F02 UF-03): `?new=1`, plus `&delivery=<uuid>` to
 *  pre-select a delivery. A malformed delivery id is ignored (plain New Run). */
export function readNewRunLink(params: URLSearchParams): { deliveryId: string | null; growerId: string | null; variety: string | null } | null {
  if (params.get('new') !== '1') return null
  const d = params.get('delivery')
  const g = params.get('grower')
  const v = params.get('variety')?.trim()
  return {
    deliveryId: d && UUID_RE.test(d) ? d : null,
    // UF-09: a berry run started from the waiting dollies. A malformed grower id is ignored.
    growerId:   g && UUID_RE.test(g) ? g : null,
    variety:    v ? v : null,
  }
}

/** Href for "Start a run" from a delivery (F01 UF-02 uses this), or from a
 *  grower + variety (F02 UF-09, the berry waiting-dollies list). */
export function newRunHref(deliveryId?: string | null, start?: { growerId: string; variety: string } | null): string {
  if (deliveryId) return `/runs?new=1&delivery=${encodeURIComponent(deliveryId)}`
  if (start?.growerId && start.variety) {
    return `/runs?new=1&grower=${encodeURIComponent(start.growerId)}&variety=${encodeURIComponent(start.variety)}`
  }
  return '/runs?new=1'
}

/** A released cooling batch (dolly), as far as New Run needs it. */
export interface WaitingDollyRow { grower: string | null; grower_id: string | null; variety: string | null }
export interface WaitingDollyGroup { key: string; grower: string; growerId: string | null; variety: string; count: number }

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

/** F02 UF-09: released dollies that no ACTIVE run matches, grouped by grower and
 *  variety, biggest group first. A dolly with no grower or no variety cannot seed a
 *  run, so it is left out of the groups. */
export function groupWaitingDollies(
  dollies: WaitingDollyRow[],
  activeRuns: { grower: string | null; variety: string | null }[],
): WaitingDollyGroup[] {
  const covered = new Set(activeRuns.map(r => `${norm(r.grower)}|${norm(r.variety)}`))
  const map = new Map<string, WaitingDollyGroup>()
  for (const d of dollies) {
    if (!d.grower?.trim() || !d.variety?.trim()) continue
    const key = `${norm(d.grower)}|${norm(d.variety)}`
    if (covered.has(key)) continue
    const g = map.get(key)
    if (g) { g.count += 1; if (!g.growerId && d.grower_id) g.growerId = d.grower_id }
    else map.set(key, { key, grower: d.grower.trim(), growerId: d.grower_id, variety: d.variety.trim(), count: 1 })
  }
  return [...map.values()].sort((a, b) => b.count - a.count || a.grower.localeCompare(b.grower))
}

/** F02 UF-09: does a released dolly belong to this run? Same grower, and the same
 *  variety. A DUO run draws from BOTH of its fruits, so for it the dolly's variety
 *  may also be any variety of the DUO pack's two fruits (`duoVarieties`). */
export function dollyMatchesRun(
  dolly: { grower: string | null; variety: string | null },
  run: { grower: string | null; variety: string | null },
  duoVarieties?: ReadonlySet<string> | null,
): boolean {
  if (norm(dolly.grower) !== norm(run.grower)) return false
  if (norm(dolly.variety) === norm(run.variety)) return true
  return !!duoVarieties && [...duoVarieties].some(v => norm(v) === norm(dolly.variety))
}

/** F02 UF-09: the board's dollies, this run's first (release order kept), the rest apart. */
export function splitDolliesForRun<T extends { grower: string | null; variety: string | null }>(
  dollies: T[],
  run: { grower: string | null; variety: string | null },
  duoVarieties?: ReadonlySet<string> | null,
): { mine: T[]; others: T[] } {
  const mine: T[] = [], others: T[] = []
  for (const d of dollies) (dollyMatchesRun(d, run, duoVarieties) ? mine : others).push(d)
  return { mine, others }
}

/** True when any filter narrows the list — decides which empty state is honest. */
export function filtersActive(status: RunStatusFilter, term: string): boolean {
  return status !== 'ALL' || searchPattern(term) !== ''
}

export interface PackoutPair {
  /** packed ÷ tipped, the ops figure (runs.packout_percent); null before any tip. */
  ofTipped:  number | null
  /** packed ÷ (packed + rejects + waste), the grower-facing figure; null when the
   *  run has no reject evidence and moved real fruit — then say so, never 100 %. */
  grower:    number | null
  capture:   RejectCapture
  /** The ops figure sits below the 85 % attention line AND the run is settled.
   *  U17 UF-09: a live run packs behind its tipping by design, so it is never "low". */
  lowOfTipped: boolean
  /** The run is Complete, Verified or Archived: the ops figure is final. */
  settled: boolean
}

/** U17 UF-09: the packout colour (amber or green) means something only once the run
 *  is no longer being fed or packed. Anything not open, and not unknown, is settled. */
export function packoutSettled(status: string | null | undefined): boolean {
  return status === 'COMPLETE' || status === 'VERIFIED' || status === 'ARCHIVED'
}

export function packoutPair(run: PackoutRun & { packout_percent: number | null; status?: string | null }): PackoutPair {
  const capture = rejectCapture(run)
  const grower = capture === 'NOT_CAPTURED'
    ? null
    : packoutOfMassOut(run.packed_weight_kg ?? 0, run.reject_weight_kg ?? 0, run.waste_weight_kg ?? 0)
  const ofTipped = run.packout_percent != null ? Number(run.packout_percent) : null
  const settled = packoutSettled(run.status)
  return { ofTipped, grower, capture, settled, lowOfTipped: settled && ofTipped != null && ofTipped < 85 }
}

export const fmtPct = (v: number | null): string => v == null ? '—' : `${Number(v).toFixed(1)}%`

export interface RunChipInput {
  run_id:         string
  run_kind?:      string | null
  duo_format_id?: string | null
  line_id?:       string | null
  pack_lines?:    { name: string | null } | { name: string | null }[] | null
}

/** Facts a card or row wears beside the status: bulk sort, DUO, the pack line, "from prepacked". */
export function runChips(r: RunChipInput, prepacked: ReadonlySet<string>): string[] {
  const chips: string[] = []
  if (r.run_kind === 'BULK_SORT') chips.push('Bulk sort')
  if (r.duo_format_id) chips.push('DUO')
  const line = Array.isArray(r.pack_lines) ? r.pack_lines[0] : r.pack_lines
  if (line?.name) chips.push(`Line ${line.name}`)
  if (prepacked.has(r.run_id)) chips.push('From prepacked')
  return chips
}

export interface RunTableRow extends PackoutRun {
  run_id:               string
  run_number:           string
  grower:               string
  puc:                  string | null
  status:               string
  started_at:           string
  pallet_count:         number
  packout_percent:      number | null
}

/** The figures a card or row prints — computed here so the components only render. */
export interface RunCells {
  variety:      string
  puc:          string
  started:      string
  /** "Started 4 Oct 2026 · open 19 days"; a closed run shows only "Started …". */
  startedLine:  string
  /** "open 19 days", or null for a closed run. */
  open:         string | null
  tipped:       string
  packed:       string
  pallets:      string
  ofTipped:     string
  lowOfTipped:  boolean
  /** Run is settled: only then may the ops packout wear amber or green. */
  settled:      boolean
  /** The grower packout, or the reason it cannot be stated. */
  grower:       string
  growerWithheld: boolean
}

/** Whole SAST calendar days a run has been open, or null when it is closed
 *  (Complete, Verified, Archived) or the start is not a date. Age, not last
 *  feed: a run that packs for three days reads "3". Shared with F02 UF-16. */
export function daysOpen(startedAt: string | null | undefined, status: string, todayYMD: string = sastTodayYMD()): number | null {
  if (!startedAt || !(PACKING_OPEN_STATUSES as readonly string[]).includes(status)) return null
  const t = Date.parse(startedAt)
  if (Number.isNaN(t)) return null
  const d = Math.round((Date.parse(todayYMD) - Date.parse(sastYMD(t))) / 86_400_000)
  return d < 0 ? 0 : d
}

/** "open 19 days" / "open 1 day" / "open today" - null for a closed run. */
export function openLabel(days: number | null): string | null {
  if (days === null) return null
  return days === 0 ? 'open today' : `open ${days} day${days === 1 ? '' : 's'}`
}

const kg = (v: number | null | undefined) => `${Number(v ?? 0).toFixed(0)}`

export function runCells(r: RunTableRow, fmtDate: (iso: string) => string, todayYMD?: string): RunCells {
  const pair = packoutPair(r)
  const open = openLabel(daysOpen(r.started_at, r.status, todayYMD))
  const withheld = pair.capture === 'NOT_CAPTURED'
  return {
    variety:     r.variety ?? '—',
    puc:         r.puc ?? '—',
    started:     fmtDate(r.started_at),
    startedLine: `Started ${fmtDate(r.started_at)}${open ? ` · ${open}` : ''}`,
    open,
    tipped:      kg(r.tipped_net_weight_kg),
    packed:      kg(r.packed_weight_kg),
    pallets:     String(r.pallet_count),
    ofTipped:    fmtPct(pair.ofTipped),
    lowOfTipped: pair.lowOfTipped,
    settled:     pair.settled,
    grower:      withheld ? 'rejects not captured' : fmtPct(pair.grower),
    growerWithheld: withheld,
  }
}

/** Column accessors for useTableSort — a module constant, as the hook requires. */
export const RUN_TABLE_ACCESSORS: Accessors<RunTableRow> = {
  run_number: r => r.run_number,
  grower:     r => r.grower,
  variety:    r => r.variety ?? '',
  puc:        r => r.puc ?? '',
  status:     r => r.status,
  started_at: r => r.started_at,
  tipped:     r => r.tipped_net_weight_kg ?? 0,
  packed:     r => r.packed_weight_kg ?? 0,
  pallets:    r => r.pallet_count,
  packout:    r => r.packout_percent,
  grower_packout: r => packoutPair(r).grower,
}

/** What the server's delete_run reads about the seat and the run. */
export interface RunDeleteCtx {
  /** me.role: the sticky SUPER_ADMIN overlay. delete_run opens for users.role SUPER_ADMIN only. */
  role:          UserRole | null | undefined
  /** me.tenant_role: what current_tenant_role() returns (use tenantRoleOf(me)). */
  tenantRole:    UserRole | null | undefined
  runKind:       string | null | undefined
  /** is_platform_admin(): the session is in platform view. */
  platformView:  boolean
  /** in_support_view(): a support view is read-only. */
  supportView:   boolean
  /**
   * Whether a pallet of this run ever stood in a coldroom, as far as the client
   * can see (IN_COLDROOM now or stocked_at set, soft-deleted pallets included).
   * `undefined` = not known yet. The server also counts an IN_COLDROOM entry in
   * pallets_audit, which the client cannot read: a run whose only placement is
   * in that trail still shows the button and the server refuses it.
   */
  everPlaced:    boolean | undefined
}

/** delete_run's RUNLEVEL seat rule: a platform admin, or ADMIN / MANAGER of the business. */
export function canDeletePlacedRun(c: Pick<RunDeleteCtx, 'tenantRole' | 'platformView' | 'supportView'>): boolean {
  if (c.supportView) return false
  return c.platformView || c.tenantRole === 'ADMIN' || c.tenantRole === 'MANAGER'
}

/**
 * Whether the run page offers **Delete run**: exactly where delete_run (mig
 * 20261033200000 + 20261206000000, F07 RUNDEL + RUNLEVEL) would not refuse. supabase/tests and
 * runsRegister.test.ts pin each clause to the SQL text.
 *   1. users.role SUPER_ADMIN ("Only super admins can delete runs").
 *   1b. Never in a support view, and a live seat in the business or platform
 *      view (mig 20261206000000: "You need a seat in this business...").
 *   2. Never a received (EXTERNAL_RECEIPT) run: it decides who is billed for
 *      cold storage and closes by voiding its pallets.
 *   3. A run holding a pallet that ever stood in a coldroom: only a platform
 *      admin or ADMIN / MANAGER of the business; a support view never. A run
 *      with none stays with any super admin. While that is unknown the button
 *      stays hidden for a seat the rule would refuse.
 */
export function offersRunDelete(c: RunDeleteCtx): boolean {
  if (!isSuperAdmin(c.role)) return false
  if (!holdsHardDeleteSeat(c)) return false
  if (isExternalReceiptRun(c.runKind)) return false
  if (canDeletePlacedRun(c)) return true
  return c.everPlaced === false && !c.supportView
}
